import styled from 'styled-components';
import { breakpoints } from '~/singlePageApp/screens/LiveStreamScreen/shared/breakpoints';
import { Row as VMARow } from '~/singlePageApp/components/Grid';
import { isNonEmptyString, isNil } from '~/utilities/type-guards';
import { Text, Image } from '@wistia/vhs';
import { useLoadFeatureFlags } from '~/singlePageApp/hooks/useLoadFeatureFlags';
import type { JSX } from 'react';

const Container = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
  justify-content: center;
  margin-inline: 1.25rem;
`;

// This is a less heavy-handed version of the EmptyState/IllustrationCard component,
// which didn't have the flexibilty needed to build out the designs for this feature.
const Card = styled.div`
  --breakpoint: ${breakpoints.mobile}rem;

  background-color: #ffffff;
  border-radius: 0.5rem;
  box-shadow: ${({ theme }) => theme.shadow.elevation1};
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-block: 2rem;
  max-width: 50rem;
  padding: 1.5rem 2rem;
`;

const Illustration = styled(Image)`
  flex: 0;
  height: 11.5rem;
  margin: 0 auto;
  object-fit: scale-down;
  padding: 0 1rem 0 0;
  width: 100%;
`;

const Row = styled(VMARow)`
  /* align-items: center; */
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
  padding: 1.5rem 1rem 0 0;
`;

// This flex item uses an intrinsic responsive technique oddly known as
// the "Holy Albatross". It is a width calculation that determines
// when a flex item (with a fluid width) should wrap. - Josh
const Content = styled.div`
  display: flex;
  flex: 1;
  flex-basis: calc((var(--breakpoint) - 100%) * 999);

  & > * {
    margin: 0;
  }
`;

const Title = styled(Text)`
  padding-bottom: 1rem;
`;

/**
 * Generic message component to show a message to the user about the event
 *
 * @param headline - The headline to display on the card
 * @param message - The message to display on the card
 * @param subMessage - The sub message to display on the card
 * @param buttons - The buttons to render on the card
 */
export const EventMessageCard = ({
  headline,
  message,
  subMessage,
  buttons,
  illustration,
  shouldShowButtonRow,
}: {
  headline: string;
  message: string;
  subMessage?: string;
  buttons: JSX.Element[];
  illustration: string | null;
  shouldShowButtonRow: boolean;
}): JSX.Element => {
  const { flags } = useLoadFeatureFlags(['flag_vma_brand_refresh']);

  const isBrandRefreshEnabled = flags.flag_vma_brand_refresh.enabled;

  return (
    <Container>
      <Card style={isBrandRefreshEnabled ? { maxWidth: '33.25rem' } : { maxWidth: '100%' }}>
        <Content>
          {isBrandRefreshEnabled || isNil(illustration) ? null : (
            <Illustration
              alt=""
              src={illustration}
            />
          )}
          <div>
            <Title variant="headline1">{headline}</Title>
            <Text variant="body2">{message}</Text>
            {isNonEmptyString(subMessage) ? <Text variant="body2">{subMessage}</Text> : null}
            {shouldShowButtonRow ? <Row>{buttons}</Row> : null}
          </div>
        </Content>
      </Card>
    </Container>
  );
};
